import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { Button, IconButton, TextField } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { AddCircleOutlineOutlined, DragIndicator, HighlightOffOutlined } from '@material-ui/icons';
import { ConfigFieldControl } from '@sierralabs/core-ui';
const StyledList = styled.ul `
  margin: 0;
  padding: 0;
  width: 100%;
  list-style: none;
  li {
    display: flex;
    margin: 0 -12px 0 -12px;
    padding: 6px 6px 6px 0;
    &.dragging {
      opacity: 0.7;
      background: #fff;
      border-radius: 4px;
      box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2),
        0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12);
      .drag-handle {
        opacity: 1;
      }
    }
    &.add-option {
      margin-left: 0;
    }
    &:hover .drag-handle {
      opacity: 1;
    }
    .drag-handle {
      display: flex;
      align-items: center;
      opacity: 0.25;
      transition: opacity 300ms;
      svg {
        color: #bbb;
      }
    }
    .field-value {
      margin-right: 4px;
    }
    .field-label {
      margin-left: 4px;
    }
    .button-container {
      display: flex;
      align-items: center;
    }
  }
`;
export var MultiInputFieldType;
(function (MultiInputFieldType) {
    MultiInputFieldType["List"] = "List";
    MultiInputFieldType["KeyPair"] = "KeyPair";
    MultiInputFieldType["FreeForm"] = "FreeForm";
})(MultiInputFieldType || (MultiInputFieldType = {}));
function convertToValueLabel(values) {
    return values.map(value => {
        return typeof value === 'string' ? { value, label: value } : value;
    });
}
const MultiInputField = props => {
    const { options: initialOptions, type = MultiInputFieldType.List, hideTypeToggle, hideKeyPairType, onChange } = props;
    const [listType, setListType] = useState(type);
    const [freeForm, setFreeForm] = useState('');
    const [options, setOptions] = useState(() => {
        return initialOptions ? convertToValueLabel(initialOptions) : [];
    });
    const handleUpdate = (options, set = true) => {
        set && setOptions(options);
        onChange === null || onChange === void 0 ? void 0 : onChange(options);
    };
    useEffect(() => {
        setOptions(initialOptions ? convertToValueLabel(initialOptions) : []);
    }, [initialOptions]);
    return (React.createElement(React.Fragment, null,
        !hideTypeToggle && (React.createElement(ConfigFieldControl, null,
            React.createElement(ToggleButtonGroup, { exclusive: true, size: 'small', value: listType, onChange: (_, newListType) => {
                    if (listType === newListType) {
                        // no change
                        return;
                    }
                    switch (newListType) {
                        case MultiInputFieldType.List:
                            handleUpdate(options.map(({ value }) => ({ value, label: value })));
                            break;
                        case MultiInputFieldType.KeyPair:
                            if (listType === MultiInputFieldType.List) {
                                handleUpdate(options.map(o => ({
                                    value: o.value,
                                    label: o.label || o.value
                                })));
                            }
                            break;
                        case MultiInputFieldType.FreeForm:
                            if (listType === MultiInputFieldType.List) {
                                setFreeForm(options
                                    .map((option) => option.value)
                                    .join('\n'));
                            }
                            else {
                                setFreeForm(options.map(o => `${o.value}|${o.label}`).join('\n'));
                            }
                    }
                    setListType(newListType);
                } },
                React.createElement(ToggleButton, { value: MultiInputFieldType.List }, "List"),
                !hideKeyPairType && (React.createElement(ToggleButton, { value: MultiInputFieldType.KeyPair }, "Key/Pair")),
                React.createElement(ToggleButton, { value: MultiInputFieldType.FreeForm }, "Free Form")))),
        React.createElement(ConfigFieldControl, null, listType === MultiInputFieldType.FreeForm ? (React.createElement(TextField, { multiline: true, fullWidth: true, variant: 'outlined', className: 'field-value', value: freeForm, onChange: event => {
                const text = event.target.value;
                // parse key value pair
                const parsedOptions = text
                    .split('\n')
                    .map((line) => {
                    // split on first occurrence only
                    const splitIndex = line.indexOf('|');
                    // use same value if line has no split
                    const hasSplit = splitIndex > -1;
                    const key = hasSplit
                        ? line.slice(0, splitIndex)
                        : line;
                    const value = hasSplit
                        ? line.slice(splitIndex + 1, line.length)
                        : line;
                    return { value: key, label: value };
                });
                setFreeForm(text);
                handleUpdate(parsedOptions);
            } })) : (React.createElement(DragDropContext, { onDragEnd: result => {
                const { source, destination } = result;
                // dropped outside the list
                if (!destination) {
                    return;
                }
                const copy = [...options];
                const [removed] = copy.splice(source.index, 1);
                copy.splice(destination.index, 0, removed);
                handleUpdate(copy);
            } },
            React.createElement(Droppable, { droppableId: 'options' }, (provided, _snapshot) => (React.createElement(StyledList, { className: 'options-map', ref: provided.innerRef },
                options.map((option, index) => (React.createElement(Draggable, { key: index, index: index, draggableId: `draggable-${index}` }, (provided, snapshot) => (React.createElement("li", Object.assign({ ref: provided.innerRef }, provided.draggableProps, { className: snapshot.isDragging ? 'dragging' : '' }),
                    React.createElement("div", Object.assign({ className: 'drag-handle' }, provided.dragHandleProps, { tabIndex: -1 }),
                        React.createElement(DragIndicator, null)),
                    React.createElement(TextField, { fullWidth: true, size: 'small', variant: 'outlined', className: 'field-value', value: option.value, onChange: event => {
                            const { value } = event.target;
                            option.value = value;
                            handleUpdate([...options]);
                        } }),
                    listType === MultiInputFieldType.KeyPair && (React.createElement(TextField, { fullWidth: true, size: 'small', variant: 'outlined', value: option.label, className: 'field-label', onChange: event => {
                            option.label = event.target.value;
                            handleUpdate([...options]);
                        } })),
                    React.createElement("div", { className: 'button-container' },
                        React.createElement(IconButton, { size: 'small', tabIndex: '-1', onClick: () => {
                                const copy = [...options];
                                copy.splice(index, 1);
                                handleUpdate(copy);
                            } },
                            React.createElement(HighlightOffOutlined, null)))))))),
                provided.placeholder,
                React.createElement("li", { className: 'add-option' },
                    React.createElement(Button, { startIcon: React.createElement(AddCircleOutlineOutlined, null), onClick: () => {
                            handleUpdate([...options, { value: '', label: '' }]);
                        } }, "Add Option"))))))))));
};
export default MultiInputField;
//# sourceMappingURL=MultiInputField.js.map